import React, { useState, useRef, useEffect } from "react";
import { Sparkles, Send, X, Bot } from "lucide-react";
import { API } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function AIWidget({ defaultContext = "general" }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Bonjour ! Je suis MAZID AI, votre assistant intelligent. Comment puis-je vous aider aujourd'hui ?" },
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);
  const sessionRef = useRef(`session-${Date.now()}`);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, streaming]);

  const send = async () => {
    if (!input.trim() || streaming) return;
    const text = input.trim();
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const token = localStorage.getItem("mazid_token");
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          message: text,
          session_id: sessionRef.current,
          context: user?.role === "founder" ? "founder" :
                   user?.role === "finance" ? "finance" :
                   user?.role === "admin" ? "admin" :
                   user?.role === "teacher" ? "teacher" :
                   user?.role === "parent" ? "parent" : defaultContext,
        }),
      });

      if (!res.ok) {
        if (res.status === 401) {
          setMessages((m) => {
            const updated = [...m];
            updated[updated.length - 1] = { role: "assistant", content: "Veuillez vous connecter pour utiliser l'assistant MAZID AI." };
            return updated;
          });
          setStreaming(false);
          return;
        }
        throw new Error(`HTTP ${res.status}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const data = line.slice(6);
          if (data === "[DONE]") continue;
          setMessages((m) => {
            const updated = [...m];
            updated[updated.length - 1] = {
              role: "assistant",
              content: updated[updated.length - 1].content + data,
            };
            return updated;
          });
        }
      }
    } catch (e) {
      setMessages((m) => {
        const updated = [...m];
        updated[updated.length - 1] = { role: "assistant", content: "Désolé, une erreur est survenue. Veuillez réessayer." };
        return updated;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-24 right-6 z-50 w-16 h-16 rounded-full bg-[#EA580C] text-white shadow-2xl flex items-center justify-center animate-mazid-pulse hover:scale-110 transition-transform"
          data-testid="ai-widget-open"
          aria-label="Ouvrir MAZID AI"
        >
          <span className="absolute inset-0 rounded-full bg-[#EA580C]/40 animate-ping-slow" />
          <Sparkles className="h-7 w-7 relative z-10" />
        </button>
      )}

      {open && (
        <div className="fixed bottom-24 right-6 z-50 w-[92vw] sm:w-[400px] h-[600px] max-h-[75vh] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden" data-testid="ai-widget-panel">
          <div className="bg-gradient-to-r from-[#166534] to-[#1E3A8A] text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center">
                <Bot className="h-5 w-5" />
              </div>
              <div>
                <div className="font-heading font-bold text-sm">MAZID AI ASSISTANT</div>
                <div className="text-[10px] opacity-80 tracking-wider uppercase">Claude · En ligne</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="p-1.5 rounded-md hover:bg-white/10" data-testid="ai-widget-close">
              <X className="h-4 w-4" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[#1E3A8A] text-white rounded-tr-sm"
                      : "bg-white border border-slate-200 text-slate-800 rounded-tl-sm"
                  }`}
                  data-testid={`ai-msg-${m.role}-${i}`}
                >
                  {m.content || (streaming && i === messages.length - 1 ? "..." : "")}
                </div>
              </div>
            ))}
          </div>

          <div className="border-t border-slate-200 p-3 bg-white flex gap-2 items-end">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder="Posez une question..."
              rows={1}
              className="flex-1 resize-none border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#166534]/30 focus:border-[#166534]"
              data-testid="ai-widget-input"
            />
            <button
              onClick={send}
              disabled={streaming || !input.trim()}
              className="p-2.5 rounded-lg bg-[#EA580C] text-white hover:bg-orange-700 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="ai-widget-send"
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
